import React, { useState, useEffect, useCallback } from 'react';
import tripFileManager from '../utils/tripFileManager';

const AutoSaveIndicator = ({ tripId }) => {
  const [fsaStatus, setFsaStatus]           = useState('idle');   // saving|saved|error|idle
  const [opfsStatus, setOpfsStatus]         = useState('idle');   // saved|idle
  const [lastSaved, setLastSaved]           = useState(null);
  const [needsPermission, setNeedsPermission] = useState(false);
  const [requesting, setRequesting]         = useState(false);

  // FSA auto-save events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail.tripId !== tripId) return;
      setFsaStatus(e.detail.status);
      if (e.detail.lastSaved) setLastSaved(e.detail.lastSaved);
      if (e.detail.status === 'saved') setTimeout(() => setFsaStatus('idle'), 3000);
    };
    window.addEventListener('trip-autosave-status', handler);
    return () => window.removeEventListener('trip-autosave-status', handler);
  }, [tripId]);

  // OPFS save events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail.tripId !== tripId) return;
      if (e.detail.status === 'saved') {
        setOpfsStatus('saved');
        setLastSaved(Date.now());
        setTimeout(() => setOpfsStatus('idle'), 4000);
      }
    };
    window.addEventListener('wayfare-opfs-status', handler);
    return () => window.removeEventListener('wayfare-opfs-status', handler);
  }, [tripId]);

  // FSA permission-needed events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail.tripIds?.includes(tripId)) setNeedsPermission(true);
    };
    window.addEventListener('wayfare-fsa-permission-needed', handler);
    // Also check current state on mount
    if (tripFileManager.getStatus(tripId).needsPermission) setNeedsPermission(true);
    return () => window.removeEventListener('wayfare-fsa-permission-needed', handler);
  }, [tripId]);

  const handleReactivate = useCallback(async () => {
    setRequesting(true);
    try {
      const granted = await tripFileManager.requestPendingPermissions();
      if (granted > 0) setNeedsPermission(false);
    } finally {
      setRequesting(false);
    }
  }, []);

  const timeSince = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - ts;
    if (diff < 60000) return 'ahora';
    if (diff < 3600000) return `hace ${Math.floor(diff / 60000)} min`;
    return `hace ${Math.floor(diff / 3600000)}h`;
  };

  if (!tripId) return null;

  const hasFsa = tripFileManager.getStatus(tripId).hasFileHandle;

  // Permission warning takes priority
  if (needsPermission) return (
    <button
      onClick={handleReactivate}
      disabled={requesting}
      style={{ ...styles.pill('#e65100', '#fff3e0'), cursor: 'pointer', border: '1px solid #e65100' }}
    >
      <span>{requesting ? '⏳' : '⚠️'}</span>
      <span>{requesting ? 'Reactivando...' : 'Reactivar guardado automático'}</span>
    </button>
  );

  // Actively writing right now
  if (fsaStatus === 'saving') return (
    <div style={styles.pill('#f57c00', '#fff3e0')} title="Guardando...">
      <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
      <span>Guardando...</span>
      <Keyframes />
    </div>
  );

  // Write failed
  if (fsaStatus === 'error') return (
    <div
      style={{ ...styles.pill('#c62828', '#ffebee'), cursor: 'pointer' }}
      title="Error al guardar — toca para más info"
      onClick={() => alert('Error al guardar.\nVerifica permisos y espacio disponible.')}
    >
      <span>⚠️</span><span>Error al guardar</span>
    </div>
  );

  // Just saved (any layer)
  if (fsaStatus === 'saved' || opfsStatus === 'saved') return (
    <div style={styles.pill('#2e7d32', '#e8f5e9')} title={lastSaved ? `Guardado ${timeSince(lastSaved)}` : 'Todo guardado'}>
      <span>✓</span><span>Todo guardado</span>
    </div>
  );

  // FSA idle (file linked, waiting for next change)
  if (hasFsa) return (
    <div style={styles.pill('#1565c0', '#e3f2fd')} title={lastSaved ? `Último guardado ${timeSince(lastSaved)}` : 'Guardado automático activo'}>
      <span>✓</span><span>Todo guardado</span>
    </div>
  );

  // Default: OPFS protection active (always true when a trip is open)
  return (
    <div style={styles.pill('#4a7c59', '#edf7f0')} title="Tus datos se guardan automáticamente en tu dispositivo">
      <span>✓</span><span>Todo guardado</span>
    </div>
  );
};

const styles = {
  pill: (color, bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.35rem 0.7rem',
    background: bg,
    color,
    borderRadius: '20px',
    fontSize: '0.72rem',
    fontWeight: '600',
    border: `1px solid ${color}`,
    transition: 'all 0.3s ease',
    userSelect: 'none',
  }),
};

const Keyframes = () => (
  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
);

export default AutoSaveIndicator;
